
import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { FormField, FormItem, FormLabel, FormControl, FormDescription, Form } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { DatePicker } from '@/components/ui/date-picker';
import { UseFormReturn } from 'react-hook-form';
import { Trophy, Calendar, Users } from 'lucide-react';

interface LeaderboardSettingsSectionProps {
  form: UseFormReturn<any>;
}

export const LeaderboardSettingsSection: React.FC<LeaderboardSettingsSectionProps> = ({ form }) => {
  const leaderboardPrizes = [
    { value: 'gift-card', label: 'Gift Card' },
    { value: 'store-credit', label: 'Store Credit' },
    { value: 'discount', label: 'Discount Code' },
    { value: 'physical-prize', label: 'Physical Prize' },
    { value: 'cash', label: 'Cash Reward' }
  ];

  const visibilityOptions = [
    { value: 'public', label: 'Public (Visible to all users)' },
    { value: 'private', label: 'Private (Visible only to participants)' },
    { value: 'top-only', label: 'Top Only (Only top 10 visible)' }
  ];

  // Initialize leaderboardSettings if it doesn't exist
  React.useEffect(() => {
    if (!form.getValues().leaderboardSettings) {
      form.setValue('leaderboardSettings', {
        contestPeriod: 'monthly',
        startDate: new Date(),
        endDate: new Date(new Date().setMonth(new Date().getMonth() + 1)),
        visibilityType: 'public',
        topPositionsCount: 10,
        firstPlacePrize: '100',
        secondPlacePrize: '50',
        thirdPlacePrize: '25',
        prizeType: 'gift-card',
        minimumReferrals: 3,
        showRealTimeUpdates: true,
        allowTies: false,
        displayUserProfiles: true
      });
    }
  }, [form]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Trophy className="mr-2 h-5 w-5 text-yellow-500" />
          Leaderboard Configuration
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Form {...form}>
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="leaderboardSettings.contestPeriod"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Contest Period</FormLabel>
                  <Select 
                    value={field.value} 
                    onValueChange={field.onChange}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select contest period" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="custom">Custom Period</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    How often the leaderboard contest resets
                  </FormDescription>
                </FormItem>
              )}
            />

            {form.watch('leaderboardSettings.contestPeriod') === 'custom' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="leaderboardSettings.startDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center">
                        <Calendar className="mr-2 h-4 w-4" />
                        Start Date
                      </FormLabel>
                      <FormControl>
                        <DatePicker
                          date={field.value ? new Date(field.value) : undefined}
                          setDate={(date) => field.onChange(date)}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="leaderboardSettings.endDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center">
                        <Calendar className="mr-2 h-4 w-4" />
                        End Date
                      </FormLabel>
                      <FormControl>
                        <DatePicker
                          date={field.value ? new Date(field.value) : undefined}
                          setDate={(date) => field.onChange(date)}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            )}

            <FormField
              control={form.control}
              name="leaderboardSettings.visibilityType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Leaderboard Visibility</FormLabel>
                  <Select 
                    value={field.value} 
                    onValueChange={field.onChange}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select visibility type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {visibilityOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Who can view the leaderboard
                  </FormDescription>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="leaderboardSettings.topPositionsCount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center">
                    <Users className="mr-2 h-4 w-4" />
                    Number of Top Positions
                  </FormLabel>
                  <FormControl>
                    <Input {...field} type="number" min="3" max="100" />
                  </FormControl>
                  <FormDescription>
                    How many top performers to display on the leaderboard
                  </FormDescription>
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 pb-2">
              <FormField
                control={form.control}
                name="leaderboardSettings.firstPlacePrize"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>1st Place Prize</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g. 100" />
                    </FormControl>
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="leaderboardSettings.secondPlacePrize"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>2nd Place Prize</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g. 50" />
                    </FormControl>
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="leaderboardSettings.thirdPlacePrize"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>3rd Place Prize</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g. 25" />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="leaderboardSettings.prizeType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Prize Type</FormLabel>
                  <Select 
                    value={field.value} 
                    onValueChange={field.onChange}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select prize type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {leaderboardPrizes.map(prize => (
                        <SelectItem key={prize.value} value={prize.value}>
                          {prize.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Type of reward for leaderboard winners
                  </FormDescription>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="leaderboardSettings.minimumReferrals"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Minimum Referrals to Qualify</FormLabel>
                  <FormControl>
                    <Input {...field} type="number" min="1" max="100" />
                  </FormControl>
                  <FormDescription>
                    Minimum number of successful referrals needed to appear on the leaderboard
                  </FormDescription>
                </FormItem>
              )}
            />

            <div className="space-y-4 pt-4">
              <FormField
                control={form.control}
                name="leaderboardSettings.showRealTimeUpdates"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-md p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        Real-Time Updates
                      </FormLabel>
                      <FormDescription>
                        Show leaderboard positions in real-time
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="leaderboardSettings.allowTies"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-md p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        Allow Tied Positions
                      </FormLabel>
                      <FormDescription>
                        Allow multiple users to share the same rank if they have equal referrals
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="leaderboardSettings.displayUserProfiles"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-md p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        Display User Profiles
                      </FormLabel>
                      <FormDescription>
                        Show user profile pictures on the leaderboard (if available)
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>
        </Form>
      </CardContent>
    </Card>
  );
};

export default LeaderboardSettingsSection;
